import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { API_ORIGIN } from '@/constants'
import { authorizedFetch, getAuthAccessToken } from '@/lib/api'

export type EvaluationAnalysisDimension = {
  key: string
  label: string
  score: number
  reasoning?: string
  weight?: number
}

export type EvaluationCriterionResult = {
  key: string
  kind?: string
  label: string
  score?: number
  reasoning?: string
  weight?: number
  met?: boolean
}

export type EvaluationRubricSnapshot = {
  methodology_id?: string
  revision_version?: string | number
  corpus_slug?: string
  skill_domains?: Array<{ key?: string; label?: string; skills?: string[] }>
}

export type CandidateDomainSkill = {
  display: string
  canonical: string
  match_type: 'exact' | 'alias' | 'semantic'
  confidence?: number
}

export type CandidateSkillsDomain = {
  domain_key?: string
  domain_label?: string
  skills: CandidateDomainSkill[]
}

export type UnmappedSkill = {
  display: string
  reason?: string
  nearest?: string
  confidence?: number
}

export type EvaluationAnalysisPayload = {
  headline: string
  schema_version?: string
  methodology_id?: string
  dimensions: EvaluationAnalysisDimension[]
  criteria_results?: EvaluationCriterionResult[]
  rubric_snapshot?: EvaluationRubricSnapshot
  candidate_skills_by_domain?: CandidateSkillsDomain[]
  unmapped_skills?: UnmappedSkill[]
  requirements: { required_skills: string[]; preferred_skills: string[] }
  findings: { highlights: string[]; red_flags: string[] }
  narrative: { summary?: string }
  scoring_trace: {
    component_scores: Record<string, number>
    final_score: number
    was_llm_judged: boolean
  }
  research_sources?: Array<{ url: string; title: string; snippet?: string }>
  disclaimer?: string
  evidence_summary?: Record<string, number>
}

export type EvaluationAnalysisResponse = {
  id: string
  methodology_id: string
  methodology_version: string
  payload: EvaluationAnalysisPayload
  status: string
}

export type EnrichmentStage = {
  stage: string
  message?: string
}

export type UseEvaluationAnalysisOptions = {
  enabled?: boolean
}

export function useEvaluationAnalysis(
  evaluationId: string | null | undefined,
  options: UseEvaluationAnalysisOptions = {},
) {
  const { enabled = true } = options
  const queryClient = useQueryClient()
  const [enrichmentStage, setEnrichmentStage] = useState<EnrichmentStage | null>(null)
  const [isEnriching, setIsEnriching] = useState(false)
  const streamAbortRef = useRef<AbortController | null>(null)

  const query = useQuery({
    queryKey: ['evaluation-analysis', evaluationId],
    queryFn: async (): Promise<EvaluationAnalysisResponse | null> => {
      if (!evaluationId) return null

      const response = await authorizedFetch(
        `${API_ORIGIN}/api/evaluations/${encodeURIComponent(evaluationId)}/analysis`,
      )
      if (response.status === 404) {
        return null
      }
      if (!response.ok) {
        throw new Error('Failed to load evaluation analysis')
      }
      return response.json()
    },
    enabled: Boolean(evaluationId) && enabled,
    staleTime: 60_000,
  })

  useEffect(() => {
    streamAbortRef.current?.abort()
    streamAbortRef.current = null

    if (!evaluationId || !enabled) {
      setIsEnriching(false)
      setEnrichmentStage(null)
      return
    }

    const status = query.data?.status
    if (status !== 'pending' && status !== 'running') {
      setIsEnriching(false)
      setEnrichmentStage(null)
      return
    }

    setIsEnriching(true)
    const abortController = new AbortController()
    streamAbortRef.current = abortController
    const token = getAuthAccessToken()

    void fetchEventSource(
      `${API_ORIGIN}/api/evaluations/${encodeURIComponent(evaluationId)}/analysis/stream`,
      {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: abortController.signal,
        onmessage(event) {
          if (!event.data?.trim()) return
          try {
            const data = JSON.parse(event.data) as {
              type?: string
              stage?: string
              message?: string
              payload?: EvaluationAnalysisPayload
              analysis_id?: string
            }

            if (data.type === 'evaluation_enrichment_progress') {
              setEnrichmentStage({
                stage: data.stage || 'progress',
                message: data.message,
              })
            }

            if (data.type === 'evaluation_enrichment_complete' && data.payload) {
              queryClient.setQueryData(
                ['evaluation-analysis', evaluationId],
                (prev: EvaluationAnalysisResponse | null | undefined) =>
                  prev
                    ? { ...prev, status: 'complete', payload: data.payload as EvaluationAnalysisPayload }
                    : {
                        id: data.analysis_id || '',
                        methodology_id: '',
                        methodology_version: '1',
                        payload: data.payload as EvaluationAnalysisPayload,
                        status: 'complete',
                      },
              )
              setIsEnriching(false)
              setEnrichmentStage(null)
            }

            if (data.type === 'evaluation_enrichment_failed') {
              setIsEnriching(false)
              queryClient.setQueryData(
                ['evaluation-analysis', evaluationId],
                (prev: EvaluationAnalysisResponse | null | undefined) =>
                  prev ? { ...prev, status: 'failed' } : prev,
              )
            }
          } catch {
            // ignore malformed SSE payloads
          }
        },
        onerror() {
          if (!abortController.signal.aborted) {
            setIsEnriching(false)
          }
        },
      },
    ).catch(() => {
      if (!abortController.signal.aborted) {
        setIsEnriching(false)
      }
    })

    return () => {
      abortController.abort()
    }
  }, [evaluationId, enabled, query.data?.status, queryClient])

  return {
    analysis: query.data,
    payload: query.data?.payload ?? null,
    status: query.data?.status ?? null,
    isLoading: query.isLoading,
    isMissing: !query.isLoading && query.isSuccess && query.data === null,
    isEnriching: isEnriching || query.data?.status === 'running',
    enrichmentStage,
    error: query.error,
    refetch: query.refetch,
  }
}
